/**
 * Dashboard Templates Configuration
 * ==================================
 * Industry-specific and role-specific dashboard layouts
 * Defines section order and visibility for different user contexts
 */

// All available dashboard sections
export const ALL_SECTIONS = [
  { id: 'insights', label: 'Corvana AI & Greeting', category: 'intelligence', defaultPriority: 'critical' },
  { id: 'seasonal', label: 'Seasonal Outlook', category: 'intelligence', defaultPriority: 'high' },
  { id: 'wins', label: 'Recent Wins', category: 'performance', defaultPriority: 'high' },
  { id: 'concerns', label: 'Business Concerns', category: 'alerts', defaultPriority: 'critical' },
  { id: 'opportunities', label: 'Opportunities', category: 'growth', defaultPriority: 'high' },
  { id: 'financial', label: 'Financial Intelligence', category: 'finance', defaultPriority: 'critical' },
  { id: 'performance', label: 'Performance & Feedback', category: 'operations', defaultPriority: 'medium' },
  { id: 'market', label: 'Market & Operations', category: 'market', defaultPriority: 'medium' },
  { id: 'planning', label: 'Planning & Context', category: 'planning', defaultPriority: 'medium' },
  { id: 'kpis', label: 'KPI Dashboard', category: 'metrics', defaultPriority: 'high' },
  { id: 'live-benchmarks', label: 'Live Industry Benchmarks', category: 'market', defaultPriority: 'high' },
  { id: 'live-metrics', label: 'Live Metrics', category: 'metrics', defaultPriority: 'medium' },
  { id: 'tasks', label: 'Tasks & Recommendations', category: 'operations', defaultPriority: 'high' },
  { id: 'upsell', label: 'Unlock More Value', category: 'growth', defaultPriority: 'low' },
  { id: 'quick-stats', label: 'Quick Stats', category: 'metrics', defaultPriority: 'low' },
  { id: 'activity', label: 'Recent Activity', category: 'activity', defaultPriority: 'low' },
  { id: 'quick-links', label: 'Quick Links', category: 'navigation', defaultPriority: 'low' },
];

// Industry-specific dashboard layouts
// Order determines display order, hidden array contains sections hidden by default
export const INDUSTRY_TEMPLATES = {
  // Hospitality: Focus on daily operations, food cost, staffing
  hospitality: {
    name: 'Hospitality',
    description: 'Optimized for restaurants, cafes, bars, and food service',
    order: [
      'insights',
      'concerns',        // Food cost alerts, staffing issues first
      'live-metrics',    // Real-time covers, reservations
      'kpis',            // Daily revenue, food cost %, labor cost %
      'live-benchmarks', // Live industry benchmarks from ATO, AHA, R&CA
      'performance',     // Staff performance critical in hospitality
      'financial',
      'tasks',
      'wins',
      'opportunities',
      'seasonal',        // Seasonal menu planning
      'market',
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Daily Operations', 'Food Cost Control', 'Staff Scheduling', 'Customer Reviews'],
  },

  // Healthcare: Focus on patient flow, compliance, appointments
  healthcare: {
    name: 'Healthcare',
    description: 'Optimized for medical practices, clinics, and allied health',
    order: [
      'insights',
      'concerns',        // Compliance alerts, patient wait times
      'kpis',            // Billings, patient satisfaction, wait time
      'live-benchmarks',
      'tasks',           // Patient follow-ups, referrals
      'live-metrics',    // Today's appointments
      'financial',
      'performance',     // Provider utilization
      'wins',
      'planning',        // Appointment scheduling
      'opportunities',
      'seasonal',        // Flu season, wellness checks
      'market',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Patient Care', 'Compliance', 'Billing Efficiency', 'Provider Utilization'],
  },

  // Wholesale/Distribution: Focus on inventory, suppliers, logistics
  wholesale: {
    name: 'Wholesale & Distribution',
    description: 'Optimized for wholesalers, distributors, and B2B suppliers',
    order: [
      'insights',
      'concerns',        // Stock alerts, credit exposure
      'market',          // Supplier performance, logistics
      'kpis',            // Inventory turnover, fill rate, AR days
      'live-benchmarks',
      'financial',       // Cash flow critical for wholesale
      'live-metrics',    // Orders in progress
      'tasks',           // Reorder points, deliveries
      'wins',
      'opportunities',   // Volume deals, new retailers
      'seasonal',        // Seasonal inventory planning
      'performance',
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Inventory Management', 'Supplier Relations', 'Credit Control', 'Logistics'],
  },

  // Retail: Focus on sales, conversion, inventory
  retail: {
    name: 'Retail',
    description: 'Optimized for retail stores, e-commerce, and multi-location retail',
    order: [
      'insights',
      'live-metrics',    // Today's sales, foot traffic
      'kpis',            // Conversion rate, avg transaction, shrinkage
      'live-benchmarks',
      'concerns',        // Stock-outs, theft alerts
      'wins',            // Sales achievements
      'financial',
      'tasks',
      'performance',     // Staff sales performance
      'opportunities',
      'market',          // Competitor pricing
      'seasonal',        // Holiday planning
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Sales Performance', 'Inventory Levels', 'Customer Conversion', 'Staff Productivity'],
  },

  // Professional Services: Focus on utilization, billing, projects
  professional: {
    name: 'Professional Services',
    description: 'Optimized for law firms, accountants, consultants, and agencies',
    order: [
      'insights',
      'kpis',            // Utilization, realization, WIP
      'live-benchmarks',
      'financial',       // Billings, AR, cash flow
      'tasks',           // Deadlines, deliverables
      'concerns',        // Overdue WIP, unbilled time
      'performance',     // Team utilization
      'wins',            // Closed deals, client wins
      'opportunities',   // Pipeline, proposals
      'planning',        // Resource planning
      'live-metrics',
      'market',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Billable Hours', 'Client Relationships', 'Project Delivery', 'Cash Collection'],
  },

  // Construction/Trades: Focus on projects, safety, scheduling
  construction: {
    name: 'Construction & Trades',
    description: 'Optimized for builders, contractors, and trade services',
    order: [
      'insights',
      'concerns',        // Safety alerts, project delays
      'tasks',           // Job schedules, inspections
      'kpis',            // Project margins, on-time delivery
      'live-benchmarks',
      'live-metrics',    // Active jobs, crew locations
      'financial',       // Progress billings, retentions
      'performance',     // Crew productivity
      'wins',
      'planning',        // Resource scheduling
      'opportunities',
      'market',          // Supplier quotes
      'seasonal',        // Weather impacts
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Project Management', 'Safety Compliance', 'Resource Allocation', 'Cost Control'],
  },

  // Automotive: Vehicle service and sales focus
  automotive: {
    name: 'Automotive',
    description: 'Optimized for workshops, dealerships, and auto services',
    order: [
      'insights',
      'live-metrics',    // Bays in use, jobs in progress
      'tasks',           // Service bookings, parts orders
      'kpis',            // Average repair time, revenue per bay
      'live-benchmarks',
      'concerns',        // Overdue services, warranty claims
      'financial',
      'performance',     // Technician productivity
      'wins',
      'opportunities',   // Upsell opportunities, fleet contracts
      'market',
      'planning',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Workshop Efficiency', 'Parts Management', 'Customer Service', 'Technician Productivity'],
  },

  // Beauty & Personal Care
  beauty: {
    name: 'Beauty & Personal Care',
    description: 'Optimized for salons, spas, and personal care services',
    order: [
      'insights',
      'live-metrics',    // Today's appointments
      'tasks',           // Client follow-ups, rebookings
      'kpis',            // Client retention, avg ticket
      'live-benchmarks',
      'wins',            // New clients, rebookings
      'concerns',        // Cancellations, no-shows
      'financial',
      'performance',     // Stylist productivity
      'opportunities',   // Retail upsells, membership drives
      'market',
      'seasonal',        // Wedding season, holidays
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Appointment Management', 'Client Retention', 'Retail Sales', 'Staff Bookings'],
  },

  // Fitness & Wellness
  fitness: {
    name: 'Fitness & Wellness',
    description: 'Optimized for gyms, studios, and wellness centers',
    order: [
      'insights',
      'live-metrics',    // Current attendance, class capacity
      'kpis',            // Member retention, attendance rate
      'live-benchmarks',
      'concerns',        // Cancellations, membership churn
      'tasks',           // Class schedules, equipment maintenance
      'wins',            // New members, renewals
      'financial',       // Membership revenue, PT revenue
      'performance',     // Trainer utilization
      'opportunities',   // Upsells, challenges, events
      'market',
      'seasonal',        // New year rush, summer
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Member Retention', 'Class Utilization', 'PT Revenue', 'Equipment Management'],
  },

  // Education & Training
  education: {
    name: 'Education & Training',
    description: 'Optimized for schools, tutors, and training providers',
    order: [
      'insights',
      'kpis',            // Enrollment, completion rates
      'live-benchmarks',
      'tasks',           // Course schedules, assessments
      'concerns',        // At-risk students, compliance
      'live-metrics',    // Current classes, attendance
      'financial',       // Tuition, grants
      'performance',     // Instructor ratings
      'wins',            // Graduations, certifications
      'planning',        // Term planning, curriculum
      'opportunities',   // New courses, corporate training
      'market',
      'seasonal',        // Enrollment periods
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Student Outcomes', 'Course Management', 'Compliance', 'Revenue Growth'],
  },

  // Manufacturing & Production
  manufacturing: {
    name: 'Manufacturing & Production',
    description: 'Optimized for factories, production facilities, and makers',
    order: [
      'insights',
      'concerns',        // Equipment failures, quality issues
      'live-metrics',    // Production output, line status
      'kpis',            // OEE, yield, defect rate
      'live-benchmarks',
      'tasks',           // Maintenance schedules, orders
      'financial',       // Cost per unit, material costs
      'market',          // Supplier status, logistics
      'performance',     // Shift productivity
      'planning',        // Production scheduling
      'wins',
      'opportunities',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Production Efficiency', 'Quality Control', 'Supply Chain', 'Cost Management'],
  },

  // Transport & Logistics
  transport: {
    name: 'Transport & Logistics',
    description: 'Optimized for freight, delivery, and logistics companies',
    order: [
      'insights',
      'live-metrics',    // Fleet status, deliveries in progress
      'concerns',        // Delays, maintenance due
      'kpis',            // On-time delivery, fleet utilization
      'live-benchmarks',
      'tasks',           // Routes, pickups, maintenance
      'financial',       // Fuel costs, revenue per km
      'market',          // Customer status, contracts
      'performance',     // Driver efficiency
      'planning',        // Route optimization
      'wins',
      'opportunities',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Fleet Management', 'Delivery Performance', 'Cost Control', 'Customer Service'],
  },

  // Home Services
  home_services: {
    name: 'Home Services',
    description: 'Optimized for cleaners, gardeners, and home service providers',
    order: [
      'insights',
      'tasks',           // Today's jobs, schedule
      'live-metrics',    // Jobs in progress
      'kpis',            // Jobs per day, customer satisfaction
      'live-benchmarks',
      'concerns',        // Cancellations, complaints
      'wins',            // New clients, 5-star reviews
      'financial',
      'performance',     // Team productivity
      'opportunities',   // Upsells, recurring clients
      'market',
      'planning',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Schedule Management', 'Customer Satisfaction', 'Team Productivity', 'Growth'],
  },

  // Events & Entertainment
  events: {
    name: 'Events & Entertainment',
    description: 'Optimized for venues, planners, and entertainment businesses',
    order: [
      'insights',
      'tasks',           // Upcoming events, vendor coordination
      'live-metrics',    // Today's events, capacity
      'concerns',        // Last-minute changes, vendor issues
      'kpis',            // Bookings, avg event value
      'live-benchmarks',
      'financial',       // Revenue, deposits
      'wins',            // Successful events, referrals
      'opportunities',   // Inquiries, repeat clients
      'market',          // Vendor network
      'planning',        // Event calendar
      'seasonal',        // Peak seasons
      'performance',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Event Planning', 'Vendor Management', 'Bookings Pipeline', 'Client Experience'],
  },

  // Agriculture & Primary
  agriculture: {
    name: 'Agriculture & Primary',
    description: 'Optimized for farms, nurseries, and primary producers',
    order: [
      'insights',
      'seasonal',        // Planting/harvest calendar
      'concerns',        // Weather alerts, pest issues
      'kpis',            // Yield, cost per unit
      'live-benchmarks',
      'live-metrics',    // Equipment status, crop conditions
      'tasks',           // Farm tasks, maintenance
      'financial',       // Revenue, input costs
      'market',          // Commodity prices, buyers
      'planning',        // Crop rotation, planting
      'wins',
      'opportunities',
      'performance',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Crop/Livestock Management', 'Weather Planning', 'Market Prices', 'Compliance'],
  },

  // Finance & Insurance
  finance: {
    name: 'Finance & Insurance',
    description: 'Optimized for brokers, advisors, and financial services',
    order: [
      'insights',
      'kpis',            // AUM, policies, commissions
      'live-benchmarks',
      'tasks',           // Client reviews, renewals
      'concerns',        // Compliance, expiring policies
      'financial',       // Revenue, trail commissions
      'wins',            // New clients, referrals
      'opportunities',   // Cross-sell, renewals
      'performance',     // Advisor productivity
      'market',          // Market conditions, rates
      'planning',        // Client meetings, reviews
      'live-metrics',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Client Portfolio', 'Compliance', 'Revenue Growth', 'Client Retention'],
  },

  // Non-Profit & Community
  nonprofit: {
    name: 'Non-Profit & Community',
    description: 'Optimized for charities, associations, and community groups',
    order: [
      'insights',
      'kpis',            // Donations, members, volunteers
      'live-benchmarks',
      'tasks',           // Events, campaigns, outreach
      'concerns',        // Funding gaps, compliance
      'financial',       // Donations, grants, expenses
      'wins',            // Successful campaigns, milestones
      'opportunities',   // Grant applications, sponsors
      'planning',        // Event calendar, campaigns
      'performance',     // Program outcomes
      'market',
      'live-metrics',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: ['upsell'],
    focusAreas: ['Fundraising', 'Member Engagement', 'Program Impact', 'Compliance'],
  },

  // Media & Creative
  media: {
    name: 'Media & Creative',
    description: 'Optimized for agencies, studios, and creative businesses',
    order: [
      'insights',
      'kpis',            // Project revenue, utilization
      'live-benchmarks',
      'tasks',           // Deadlines, deliverables
      'concerns',        // Overdue projects, scope creep
      'financial',       // Billings, WIP
      'wins',            // Completed projects, awards
      'performance',     // Team utilization
      'opportunities',   // Pipeline, pitches
      'planning',        // Resource allocation
      'live-metrics',
      'market',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Project Delivery', 'Creative Team', 'Client Relations', 'Pipeline'],
  },

  // Government & Public Sector
  government: {
    name: 'Government & Public',
    description: 'Optimized for councils, agencies, and public sector bodies',
    order: [
      'insights',
      'concerns',        // Compliance, service issues
      'kpis',            // Service delivery, KPIs
      'live-benchmarks',
      'tasks',           // Programs, initiatives
      'planning',        // Budget cycles, programs
      'financial',       // Budget status
      'performance',     // Team performance
      'wins',            // Milestones, achievements
      'market',
      'live-metrics',
      'opportunities',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: ['upsell', 'opportunities'],
    focusAreas: ['Service Delivery', 'Compliance', 'Budget Management', 'Program Outcomes'],
  },

  // Marine & Boating
  marine: {
    name: 'Marine & Boating',
    description: 'Optimized for marinas, boat services, and marine businesses',
    order: [
      'insights',
      'seasonal',        // Boating season, weather
      'live-metrics',    // Berth occupancy, services
      'tasks',           // Maintenance, bookings
      'kpis',            // Occupancy, service revenue
      'live-benchmarks',
      'concerns',        // Weather alerts, safety
      'financial',
      'wins',
      'opportunities',   // Seasonal promotions
      'market',
      'planning',
      'performance',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Marina Operations', 'Weather Planning', 'Seasonal Revenue', 'Customer Service'],
  },

  // Technology & Digital
  technology: {
    name: 'Technology & Digital',
    description: 'Optimized for tech companies, SaaS, and digital businesses',
    order: [
      'insights',
      'kpis',            // MRR, churn, LTV
      'live-benchmarks',
      'concerns',        // Churn risk, support tickets
      'live-metrics',    // Active users, system health
      'financial',       // Revenue, burn rate
      'tasks',           // Development, releases
      'wins',            // New customers, milestones
      'opportunities',   // Pipeline, expansions
      'performance',     // Team velocity
      'planning',        // Roadmap, sprints
      'market',
      'seasonal',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Product Metrics', 'Customer Success', 'Revenue Growth', 'Team Performance'],
  },

  // Veterinary & Animal Care
  veterinary: {
    name: 'Veterinary & Animal Care',
    description: 'Optimized for vets, groomers, and animal care services',
    order: [
      'insights',
      'concerns',        // Emergency cases, compliance
      'live-metrics',    // Today's appointments
      'tasks',           // Follow-ups, vaccinations due
      'kpis',            // Patient visits, avg transaction
      'live-benchmarks',
      'financial',       // Revenue, outstanding
      'wins',            // New clients, reviews
      'performance',     // Vet utilization
      'opportunities',   // Preventive care, products
      'planning',
      'market',
      'seasonal',        // Parasite season, holidays
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Patient Care', 'Appointment Management', 'Compliance', 'Client Retention'],
  },

  // Accommodation & Tourism
  accommodation: {
    name: 'Accommodation & Tourism',
    description: 'Optimized for hotels, B&Bs, and tourism operators',
    order: [
      'insights',
      'live-metrics',    // Current occupancy, check-ins
      'kpis',            // RevPAR, ADR, occupancy
      'live-benchmarks',
      'seasonal',        // Peak seasons, events
      'concerns',        // Maintenance, guest issues
      'tasks',           // Housekeeping, reservations
      'financial',       // Revenue, F&B
      'wins',            // Reviews, bookings
      'opportunities',   // Upsells, packages
      'performance',     // Staff performance
      'market',
      'planning',
      'activity',
      'upsell',
      'quick-stats',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Occupancy Management', 'Guest Experience', 'Revenue Optimization', 'Operations'],
  },

  // Default template for other industries
  default: {
    name: 'General Business',
    description: 'Balanced layout for all business types',
    order: [
      'insights',
      'seasonal',
      'wins',
      'concerns',
      'opportunities',
      'financial',
      'performance',
      'market',
      'planning',
      'kpis',
      'live-benchmarks',
      'live-metrics',
      'tasks',
      'upsell',
      'quick-stats',
      'activity',
      'quick-links',
    ],
    hidden: [],
    focusAreas: ['Business Overview', 'Financial Health', 'Team Performance', 'Growth Opportunities'],
  },
};

// Role-specific dashboard configurations
// Determines which sections are visible and priority for different roles
export const ROLE_TEMPLATES = {
  // Owner/Director: Full visibility, strategic focus
  owner: {
    name: 'Owner / Director',
    description: 'Complete business overview with strategic insights',
    visibleSections: 'all', // All sections visible
    prioritySections: ['insights', 'financial', 'concerns', 'kpis', 'opportunities'],
    hiddenByDefault: [],
    canCustomize: true,
    focusMessage: 'Your complete business at a glance',
  },

  // General Manager: Operations + finance focus
  manager: {
    name: 'General Manager',
    description: 'Operations and team management focus',
    visibleSections: 'all',
    prioritySections: ['insights', 'concerns', 'tasks', 'performance', 'kpis'],
    hiddenByDefault: ['upsell'],
    canCustomize: true,
    focusMessage: 'Your team and operations overview',
  },

  // Sales Manager: Pipeline and revenue focus
  sales_manager: {
    name: 'Sales Manager',
    description: 'Sales pipeline and revenue focus',
    visibleSections: [
      'insights', 'wins', 'opportunities', 'kpis', 'tasks', 
      'performance', 'financial', 'activity', 'quick-links'
    ],
    prioritySections: ['wins', 'opportunities', 'kpis', 'tasks'],
    hiddenByDefault: ['seasonal', 'market', 'planning', 'upsell'],
    canCustomize: true,
    focusMessage: 'Your sales performance and pipeline',
  },

  // Operations Manager: Day-to-day operations focus
  operations_manager: {
    name: 'Operations Manager',
    description: 'Daily operations and efficiency focus',
    visibleSections: [
      'insights', 'concerns', 'tasks', 'live-metrics', 'performance',
      'kpis', 'planning', 'activity', 'quick-links',
    ],
    prioritySections: ['concerns', 'tasks', 'live-metrics', 'performance'],
    hiddenByDefault: ['financial', 'opportunities', 'upsell', 'wins'],
    canCustomize: true,
    focusMessage: 'Your operations and team status',
  },

  // Finance Manager: Financial focus
  finance_manager: {
    name: 'Finance Manager',
    description: 'Financial performance and cash flow focus',
    visibleSections: [
      'insights', 'financial', 'kpis', 'concerns', 'tasks',
      'performance', 'activity', 'quick-links'
    ],
    prioritySections: ['financial', 'kpis', 'concerns'],
    hiddenByDefault: ['seasonal', 'market', 'planning', 'upsell', 'live-metrics'],
    canCustomize: true,
    focusMessage: 'Your financial performance overview',
  },

  // Sales Staff: Individual sales focus
  sales_staff: {
    name: 'Sales Representative',
    description: 'Personal sales targets and tasks',
    visibleSections: [
      'insights', 'tasks', 'wins', 'kpis', 'activity', 'quick-links',
    ],
    prioritySections: ['tasks', 'wins', 'kpis'],
    hiddenByDefault: ['financial', 'concerns', 'opportunities', 'performance', 'market', 'planning', 'seasonal', 'upsell', 'live-metrics', 'quick-stats'],
    canCustomize: false,
    focusMessage: 'Your sales targets and tasks for today',
  },

  // Operations Staff: Task-focused
  operations_staff: {
    name: 'Operations Team Member',
    description: 'Daily tasks and operational duties',
    visibleSections: [
      'insights', 'tasks', 'live-metrics', 'activity', 'quick-links'
    ],
    prioritySections: ['tasks', 'live-metrics'],
    hiddenByDefault: ['financial', 'concerns', 'opportunities', 'performance', 'market', 'planning', 'seasonal', 'upsell', 'kpis', 'wins', 'quick-stats'],
    canCustomize: false,
    focusMessage: 'Your tasks and duties for today',
  },

  // Kitchen/Production Staff: Operational focus
  kitchen_staff: {
    name: 'Kitchen / Production',
    description: 'Production tasks and inventory',
    visibleSections: [
      'insights', 'tasks', 'live-metrics', 'concerns', 'activity'
    ],
    prioritySections: ['tasks', 'concerns', 'live-metrics'],
    hiddenByDefault: ['financial', 'opportunities', 'performance', 'market', 'planning', 'seasonal', 'upsell', 'kpis', 'wins', 'quick-stats', 'quick-links'],
    canCustomize: false,
    focusMessage: 'Your production tasks and alerts',
  },

  // Default staff role
  staff: {
    name: 'Team Member',
    description: 'General staff dashboard',
    visibleSections: [
      'insights', 'tasks', 'activity', 'quick-links'
    ],
    prioritySections: ['tasks'],
    hiddenByDefault: ['financial', 'concerns', 'opportunities', 'performance', 'market', 'planning', 'seasonal', 'upsell', 'kpis', 'wins', 'live-metrics', 'quick-stats'],
    canCustomize: false,
    focusMessage: 'Your tasks for today',
  },
};

// Helper function to get industry template
export const getIndustryTemplate = (industry) => {
  const normalizedIndustry = industry?.toLowerCase().replace(/[^a-z]/g, '_');
  
  // Map common industry variations to templates
  const industryMap = {
    // Hospitality & Food
    'hospitality': 'hospitality',
    'food_beverage': 'hospitality',
    'restaurant': 'hospitality',
    'cafe': 'hospitality',
    'bar': 'hospitality',
    'catering': 'hospitality',
    'fast_food': 'hospitality',
    'food_service': 'hospitality',
    
    // Healthcare & Medical
    'healthcare': 'healthcare',
    'medical': 'healthcare',
    'dental': 'healthcare',
    'pharmacy': 'healthcare',
    'allied_health': 'healthcare',
    'aged_care': 'healthcare',
    'mental_health': 'healthcare',
    
    // Veterinary
    'veterinary': 'veterinary',
    'animal_care': 'veterinary',
    'pet_care': 'veterinary',
    'pet_grooming': 'veterinary',
    
    // Wholesale & Distribution
    'wholesale': 'wholesale',
    'wholesale_distribution': 'wholesale',
    'distribution': 'wholesale',
    'import_export': 'wholesale',
    'pool_spa_wholesale': 'wholesale',
    
    // Retail & E-commerce
    'retail': 'retail',
    'ecommerce': 'retail',
    'fashion': 'retail',
    'electronics': 'retail',
    'grocery': 'retail',
    
    // Professional Services
    'professional': 'professional',
    'professional_services': 'professional',
    'legal': 'professional',
    'accounting': 'professional',
    'consulting': 'professional',
    'marketing_agency': 'professional',
    
    // Construction & Trades
    'construction': 'construction',
    'trades': 'construction',
    'building': 'construction',
    'electrical': 'construction',
    'plumbing': 'construction',
    'hvac': 'construction',
    
    // Automotive
    'automotive': 'automotive',
    'car_dealership': 'automotive',
    'mechanic': 'automotive',
    'auto_repair': 'automotive',
    
    // Beauty & Personal Care
    'beauty': 'beauty',
    'beauty_personal_care': 'beauty',
    'salon': 'beauty',
    'spa': 'beauty',
    'barbershop': 'beauty',
    
    // Fitness & Wellness
    'fitness': 'fitness',
    'fitness_wellness': 'fitness',
    'gym': 'fitness',
    'yoga': 'fitness',
    'pilates': 'fitness',
    
    // Education & Training
    'education': 'education',
    'education_training': 'education',
    'school': 'education',
    'tutoring': 'education',
    'training_provider': 'education',
    
    // Manufacturing
    'manufacturing': 'manufacturing',
    'manufacturing_production': 'manufacturing',
    'factory': 'manufacturing',
    'production': 'manufacturing',
    
    // Transport & Logistics
    'transport': 'transport',
    'transport_logistics': 'transport',
    'logistics': 'transport',
    'freight': 'transport',
    'delivery': 'transport',
    
    // Home Services
    'home_services': 'home_services',
    'cleaning': 'home_services',
    'gardening': 'home_services',
    'landscaping': 'home_services',
    
    // Events & Entertainment
    'events': 'events',
    'events_entertainment': 'events',
    'entertainment': 'events',
    'venue': 'events',
    'wedding': 'events',
    
    // Agriculture
    'agriculture': 'agriculture',
    'agriculture_primary': 'agriculture',
    'farming': 'agriculture',
    'nursery': 'agriculture',
    
    // Finance & Insurance
    'finance': 'finance',
    'finance_insurance': 'finance',
    'insurance': 'finance',
    'broker': 'finance',
    'financial_services': 'finance',
    
    // Non-Profit
    'nonprofit': 'nonprofit',
    'non_profit': 'nonprofit',
    'charity': 'nonprofit',
    'community': 'nonprofit',
    
    // Media & Creative
    'media': 'media',
    'media_creative': 'media',
    'creative': 'media',
    'agency': 'media',
    'design': 'media',
    
    // Government
    'government': 'government',
    'government_public': 'government',
    'public_sector': 'government',
    'council': 'government',
    
    // Marine
    'marine': 'marine',
    'marine_boating': 'marine',
    'boating': 'marine',
    'marina': 'marine',
    
    // Technology
    'technology': 'technology',
    'technology_digital': 'technology',
    'tech': 'technology',
    'saas': 'technology',
    'software': 'technology',
    'it_services': 'technology',
    
    // Accommodation & Tourism
    'accommodation': 'accommodation',
    'accommodation_tourism': 'accommodation',
    'tourism': 'accommodation',
    'hotel': 'accommodation',
    'motel': 'accommodation',
    'bnb': 'accommodation',
  };

  const mappedIndustry = industryMap[normalizedIndustry] || 'default';
  return INDUSTRY_TEMPLATES[mappedIndustry] || INDUSTRY_TEMPLATES.default;
};

// Helper function to get role template
export const getRoleTemplate = (roleId) => {
  const normalizedRole = roleId?.toLowerCase().replace(/[^a-z_]/g, '');
  
  // Map common role variations
  const roleMap = {
    'owner': 'owner',
    'director': 'owner',
    'ceo': 'owner',
    'founder': 'owner',
    'investor': 'owner',
    'manager': 'manager',
    'general_manager': 'manager',
    'venue_operator': 'manager',
    'practice_manager': 'manager',
    'sales_manager': 'sales_manager',
    'sales_director': 'sales_manager',
    'operations_manager': 'operations_manager',
    'ops_manager': 'operations_manager',
    'finance_manager': 'finance_manager',
    'accountant': 'finance_manager',
    'cfo': 'finance_manager',
    'sales': 'sales_staff',
    'sales_rep': 'sales_staff',
    'account_manager': 'sales_staff',
    'operations': 'operations_staff',
    'supervisor': 'operations_staff',
    'kitchen': 'kitchen_staff',
    'chef': 'kitchen_staff',
    'cook': 'kitchen_staff',
    'production': 'kitchen_staff',
  };

  const mappedRole = roleMap[normalizedRole] || 'staff';
  return ROLE_TEMPLATES[mappedRole] || ROLE_TEMPLATES.staff;
};

// Get combined template based on industry and role
export const getDashboardTemplate = (industry, roleId, isOwnerOrManager = false) => {
  const industryTemplate = getIndustryTemplate(industry);
  const roleTemplate = getRoleTemplate(roleId);
  
  // Owners and managers get full industry template
  if (isOwnerOrManager || roleTemplate.visibleSections === 'all') {
    return {
      ...industryTemplate,
      ...roleTemplate,
      // Preserve industry name for display, role name separate
      name: industryTemplate.name,
      industryName: industryTemplate.name,
      roleName: roleTemplate.name,
      order: industryTemplate.order,
      hidden: roleTemplate.hiddenByDefault || [],
      canCustomize: roleTemplate.canCustomize,
    };
  }
  
  // Staff get filtered view based on role
  const visibleSet = new Set(roleTemplate.visibleSections);
  const filteredOrder = industryTemplate.order.filter(id => visibleSet.has(id));
  
  return {
    ...industryTemplate,
    ...roleTemplate,
    // Preserve industry name for display, role name separate
    name: industryTemplate.name,
    industryName: industryTemplate.name,
    roleName: roleTemplate.name,
    order: filteredOrder,
    hidden: roleTemplate.hiddenByDefault || [],
    canCustomize: roleTemplate.canCustomize,
  };
};

export default {
  ALL_SECTIONS,
  INDUSTRY_TEMPLATES,
  ROLE_TEMPLATES,
  getIndustryTemplate,
  getRoleTemplate,
  getDashboardTemplate,
};
